'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';

type ContactFormData = {
  name: string;
  email: string;
  message: string;
};

export default function HomeContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    // TODO: Backend integration — POST to contact form API or Formspree endpoint
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
    reset();
    toast.success('Message sent — thank you for getting in touch.');
  };

  return (
    <section className="py-24 lg:py-32 px-6" id="contact">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.25em] text-accent mb-5">
              Get Involved
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground leading-tight mb-6">
              Get in touch
            </h2>
            <p className="font-sans text-base leading-relaxed text-muted-foreground mb-6">
              You don&apos;t need to create an account to get in touch. Use this form to share information, ask a question or express interest in the project.
            </p>
            <p className="font-sans text-base leading-relaxed text-muted-foreground">
              If you have a Coed Parc story to share publicly, please{' '}
              <a href="/sign-up-login-screen" className="text-accent underline underline-offset-4 hover:text-foreground transition-colors">
                create an account
              </a>{' '}
              and use the Story Submission form.
            </p>
          </div>

          <div className="bg-card border border-border p-8 rounded-sm">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send size={20} className="text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-2">Message sent</h3>
                <p className="font-sans text-sm text-muted-foreground">
                  Thank you for getting in touch. We&apos;ll respond as soon as we can.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-outline mt-6 text-sm px-6 py-2.5"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Your name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input-field ${errors.name ? 'input-error' : ''}`}
                    placeholder="e.g. Margaret Williams"
                    {...register('name', { required: 'Please enter your name' })}
                  />
                  {errors.name && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={`input-field ${errors.email ? 'input-error' : ''}`}
                    placeholder="your@email.com"
                    {...register('email', {
                      required: 'Please enter your email address',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Please enter a valid email address' },
                    })}
                  />
                  {errors.email && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={5}
                    className={`input-field resize-none ${errors.message ? 'input-error' : ''}`}
                    placeholder="Tell us why you're getting in touch…"
                    {...register('message', { required: 'Please enter your message' })}
                  />
                  {errors.message && (
                    <p className="font-sans text-xs text-red-600 mt-1">{errors.message.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minWidth: '160px' }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}